import React from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';

import AppHandlers from './AppHandlers';

import DataService from 'lib/DataService';
import { regionSort } from 'lib/utils';

// Auth
import withAuth from 'hoc/withAuth';

// Components
import Header from '../Header';
import Browse from '../Browse';
import Building from '../Building';
import Brochure from '../Brochure';
import Collections from '../Collections';
import User from '../User';
import NotFound from '../404';
import {
	LoadingHeader,
	LoadingBrowse,
} from '../Loading';

// Contexts
import UserContext from 'contexts/User';
import UsersContext from 'contexts/Users';
import BrochureContext from 'contexts/Brochure';
import BuildingsContext from 'contexts/Buildings';
import FiltersContext from 'contexts/Filters';
import CollectionContext from 'contexts/Collections';

const Api = new DataService();

class App extends AppHandlers {
	componentDidMount() {
		const { t } = this.props;
		const { language } = this.props.match.params;

		if (!['fi', 'en'].includes(language)) {
			this.props.history.replace('/fi/t/');
		}

		Promise.all([
			Api.getBuildings(),
			Api.getSpaces(),
			Api.getUsers(),
			Api.getUser(),
			Api.getPictograms(),
			Api.getConfig(),
			Api.readUserCollections(),
		]).then(([_buildings, spaces, _users, user, pictograms, config, collections]) => {

			// Keep track of users that are assigned to a building
			let assignedUsers = [];
			const assignedCities = [];
			const spaceTypes = [];
			const assignedRegionIds = [];
			const assignedRegions = {
				en: [],
				fi: [],
			};
			let userHasBuildings = false;

			// Populate users with useful values
			const users = _users.map(user => {
				// Full name
				user.name = `${user.firstName} ${user.lastName}`;

				return user;
			});

			// Populate spaceType filter from spaces
			spaces.forEach(space => {
				if (!spaceTypes.find(s => s.value === space.type)) {
					spaceTypes.push({
						name: t('space-type/' + space.type),
						value: space.type,
						selected: false,
					});
				}
			})

			// User privileges
			// user.can.create.brochures()
			user.can = {
				create: {
					collections: () => ['developer', 'sales', 'editor'].some(role => user.role.includes(role)),
					brochures: () => ['developer', 'sales', 'editor'].some(role => user.role.includes(role)),
					master: () => ['developer', 'editor'].some(role => user.role.includes(role)),
				},
				read: {
					brochures: () => ['developer', 'sales', 'editor'].some(role => user.role.includes(role)),
				},
				update: {
					collections: () => ['developer', 'sales', 'editor'].some(role => user.role.includes(role)),
					brochures: () => ['developer', 'sales', 'editor'].some(role => user.role.includes(role)),
					master: () => ['developer', 'editor'].some(role => user.role.includes(role)),
				},
				delete: {
					brochures: () => ['developer', 'sales', 'editor'].some(role => user.role.includes(role)),
					master: () => ['developer', 'editor'].some(role => user.role.includes(role)),
				}
			};

			// Force user role to array
			if (typeof user.role === 'string') {
				user.role = [user.role];
			}

			Object.entries(config.buildingLocations).forEach(([locationId, locationTranslations]) => {
				assignedRegions.en.push([locationId, locationTranslations.en]);
				assignedRegions.fi.push([locationId, locationTranslations.fi]);
			});

			// Populate buildings with useful values
			const buildings = _buildings.map(building => {

				// Concat users to assigned users for filters
				assignedUsers = assignedUsers.concat(building.contacts);

				building.location.forEach(locationId => {
					!assignedRegionIds.includes(locationId) && assignedRegionIds.push(locationId);
				})

				// Check if user has buildings assigned
				if (building.contacts.includes(user.email)) {
					userHasBuildings = true;
				}

				// Add city to assignedCities for filters
				if (!assignedCities.includes(building.city) && building.city !== null) {
					assignedCities.push(building.city);
				}

				// Map contacts with user objects
				building.mappedContacts = building.contacts
					.map(contact => users.find(user => user.email === contact))
					.filter(contact => typeof contact !== 'undefined');

				// Map space ids to space objects
				// TODO: switch to mappedSpaces key
				building.spaces = building.spaces.filter(space => space.slug !== '').map(bspace => {
					const spaceData = spaces.find(space => space.slug === bspace);

					return spaceData ? spaceData : bspace;
				}).filter(space => {
					const valid = typeof space !== 'string';

					if (!valid) {
						console.log('[App.js] space filter invalid', space);
						// TODO: Inform someone about invalid unavailable space
					}

					return valid;
				});

				building.minArea = Math.min.apply(null, building.spaces.map(space => space.area));
				building.maxArea = Math.max.apply(null, building.spaces.map(space => space.area));

				building.area = null;
				building.fullData = false;


				if (building.minArea === building.maxArea) {
					building.area = building.minArea;
				} else if(building.spaces.length > 1) {
					building.area = building.minArea + '-' + building.maxArea;
				}



				return building;
			});

			// Filter out duplicate users
			assignedUsers = assignedUsers
				.filter((user, index) => assignedUsers.findIndex(user2 => user2 === user) === index)
				.map(user => _users.find(_user => _user.email === user)) // Map to user objects
				.filter(user => typeof user !== 'undefined') // Filter out users that were not in the _users array
				.sort((a, b) => a.firstName > b.firstName ? 1 : a.firstName < b.firstName ? -1 : 0);

			this.setState(state => {

				const browse = JSON.parse(JSON.stringify(state.browse));
				const cities = assignedCities.sort().map(city => ({
					name: city,
					value: city.toLowerCase(),
					selected: false,
				}));

				const regions = {
					en: assignedRegions.en.sort(regionSort).map(([value, name]) => ({
						name,
						value,
						selected: false,
					})).filter(region => assignedRegionIds.includes(region.value)),
					fi: assignedRegions.fi.sort(regionSort).map(([value, name]) => ({
						name,
						value,
						selected: false,
					})).filter(region => assignedRegionIds.includes(region.value)),
				};

				browse.filters.area.range.min = 999999990;
				browse.filters.area.range.max = 0;

				buildings.forEach(building => {
					browse.filters.area.range.min = Math.min(browse.filters.area.range.min, building.minArea);
					browse.filters.area.range.max = Math.max(browse.filters.area.range.max, building.maxArea);
				});

				// browse.filters.city.options = cities;
				browse.filters.spaceType.options = spaceTypes;
				browse.filters.region.options = regions;

				browse.filters.contact.options = assignedUsers.map(user => ({
					name: user.firstName + ' ' + user.lastName,
					value: user.email,
					selected: false
				}));

				// If user has assigned buildings, automatically add user filter
				if (userHasBuildings) {
					browse.activeFilters.push({
						name: `${user.firstName} ${user.lastName}`,
						selected: true,
						type: 'contact',
						value: user.email,
					});
				}

				const collection = collections.length > 0 ? collections[0].id : null;

				return {
					loading: false,
					buildings,
					browse,
					config,
					collections,
					collection,
					spaces,
					pictograms,
					users,
					user,
				}
			});
		});
	}

	componentWillUpdate() {
		console.log('[App.js] componentWillUpdate');
	}

	render() {
		const { language } = this.props.match.params;

		if (this.state.loading) {
			return (
				<React.Fragment>
					<LoadingHeader />
					<Switch>
						<Route exact path="/:language/t/" render={() => <LoadingBrowse browseType={this.state.browse.type} />} />
					</Switch>
				</React.Fragment>
			)
		}

		return (
			<div>
				<UserContext.Provider value={this.state.user}>
					<UsersContext.Provider value={this.state.users}>
						<BuildingsContext.Provider value={this.state.buildings}>
							<BrochureContext.Provider value={this.state.brochure}>
								<CollectionContext.Provider value={{
									collection: this.state.collection,
									collections: this.state.collections,
									collectionHandler: this.collectionHandler,
								}}>
									<FiltersContext.Provider value={{
										filters: this.state.browse.filters,
										filterOpenHandler: this.filterOpenHandler,
										filterCheckHandler: this.filterCheckHandler,
										filterRangeHandler: this.filterRangeHandler,
									}}>
										<Header
											baseUrl={this.state.config.baseUrl}
											collectionsOpen={this.state.collectionsOpen}
											collections={this.state.collections}
											collection={this.state.collection}
											logoutHandler={this.logoutHandler}
											userOpenHandler={this.userOpenHandler}
											updateBrochureHandler={this.updateBrochureHandler}
											togglePageListHandler={this.togglePageListHandler}
											toggleControlsHandler={this.toggleControlsHandler}
											toggleGeneratePdfOpen={this.toggleGeneratePdfOpen}
											collectionOpenHandler={this.collectionOpenHandler}
											textSearchHandler={this.textSearchHandler}
										/>
									</FiltersContext.Provider>
									<Switch>
										<Route exact path="/:language/t/" render={routeProps => (
											<Browse
												baseUrl={this.state.config.baseUrl}
												buildings={this.state.buildings}
												activeFilters={this.state.browse.activeFilters}
												sort={this.state.browse.sort}
												browseType={this.state.browse.type}
												browse={this.state.browse}
												toggleOnlyShowFreeHandler={this.toggleOnlyShowFreeHandler}
												changeBrowseTypeHandler={this.changeBrowseTypeHandler}
												browseSortChangeHandler={this.browseSortChangeHandler}
												removeFilterTagHandler={this.removeFilterTagHandler}
												{...routeProps}
											/>
										)} />
										<Route path="/:language/t/building/:buildingId/" render={routeProps => (
											<Building
												baseUrl={this.state.config.baseUrl}
												buildings={this.state.buildings}
												updateBuildingHandler={this.updateBuildingHandler}
												brochureType={this.state.brochureType}
												changeBrochureTypeHandler={this.changeBrochureTypeHandler}
												{...routeProps}
											/>
										)} />
										<Route path="/:language/t/brochure/:brochureId/:pageId?/" render={routeProps => (
											<Brochure
												activeSlot={this.state.activeSlot}
												brochure={this.state.brochure}
												buildings={this.state.buildings}
												pictograms={this.state.pictograms}
												pageListOpen={this.state.pageListOpen}
												controlsOpen={this.state.controlsOpen}
												generatePDFOpen={this.state.generatePDFOpen}
												config={this.state.config}
												updateBrochureHandler={this.updateBrochureHandler}
												updateBuildingHandler={this.updateBuildingHandler}
												togglePageListHandler={this.togglePageListHandler}
												toggleControlsHandler={this.toggleControlsHandler}
												toggleGeneratePdfOpen={this.toggleGeneratePdfOpen}
												activeSlotHandler={this.activeSlotHandler}
												{...routeProps}
											/>
										)} />
										<Route exact path="/:language/t/user/" render={routeProps => (
											<User userOpenHandler={this.userOpenHandler} {...routeProps} />
										)} />
										<Route exact path="/:language/t/brochure/" render={() => <Redirect to={`/${language}/t/`} />} />
										<Route exact path="/:language/t/building/" render={() => <Redirect to={`/${language}/t/`} />} />
										<Route component={NotFound} />
									</Switch>
									<Collections
										config={this.state.config}
										open={this.state.collectionsOpen}
										collection={this.state.collection}
										collections={this.state.collections}
										collectionSelectHandler={this.collectionSelectHandler}
										collectionHandler={this.collectionHandler}
										removeCollectionHandler={this.removeCollectionHandler}
										collectionOpenHandler={this.collectionOpenHandler}
									/>
								</CollectionContext.Provider>
							</BrochureContext.Provider>
						</BuildingsContext.Provider>
					</UsersContext.Provider>
				</UserContext.Provider>
			</div>
		);
	}
}

App.propTypes = {
	t: PropTypes.any,
}

export default withAuth(translate('translations')(App));
